<template>
  <div class="relative col-span-1 flex cursor-default flex-col divide-y divide-gray-200 rounded shadow ring-1 transition-all duration-100 bg-white ring-surface-900/10 dark:bg-slate-950/80 dark:ring-surface-200/20">
    <div>
      <div class="relative mb-6 flex flex-1 flex-col space-y-0">

        <div class="px-6 py-6">
          <div class="mt-2 font-semibold text-brand-headline dark:text-white text-lg">
            {{ segmentGroup.name }}
          </div>

          <div
            v-if="question"
            v-html="questionText"
            class="my-1.5 text-sm opacity-80 dark:text-white/80"></div>

          <div class="text-sm font-light text-cyan-900/60 dark:text-white/60">
            {{ new Intl.NumberFormat().format(audienceBreakdown.total) }} data points
          </div>
        </div>
        <div class="space-y-2 px-3 pt-2">
          <template v-if="needsSetup">
            <div
              class="flex justify-between gap-x-6 px-3 py-1 text-sm text-brand-placeholder dark:text-white/80">
              <div class="italic leading-4">
                No analytics data found for this segment group.
              </div>
            </div>
          </template>
          <template v-else v-for="segment in sortedSegments" :key="segment.id">
            <div v-if="loading"><Skeleton class="mb-2"></Skeleton></div>
            <div v-else class="flex items-center gap-x-2 px-3 py-1" :class="[!isSegmentSelected(segment.id) && 'opacity-50']">
              <Checkbox
                v-model="internalSelectedSegments"
                :value="segment.id"
                @update:modelValue="handleSegmentToggle"
                class="flex-shrink-0"
              />
              <div
                class="relative flex-1 after:absolute after:top-0 after:z-0 after:h-full after:border-b after:border-b-blue-500/20 after:bg-blue-500/10 after:content-[''] dark:after:bg-surface-500/20"
                :class="[
                  `after:w-${
                    audienceBreakdown.total
                      ? ((segment.count / audienceBreakdown.total) * 100).toFixed(
                          0
                        )
                      : '0.0'
                  }%`
                ]">
                <div class="relative z-10 flex justify-between items-center w-full text-sm text-brand-text dark:text-white/80 px-2 py-0.5">
                  <div class="leading-4">
                    {{ renderText(showSegmentName(segment)) }}
                  </div>
                  <div class="flex space-x-1 flex-shrink-0">
                    <div>{{ new Intl.NumberFormat().format(segment.count) }}</div>
                    <div class="opacity-30">/</div>
                    <div class="font-bold">
                      {{
                        audienceBreakdown.total
                          ? (
                              (segment.count / audienceBreakdown.total) *
                              100
                            ).toFixed(1)
                          : '0.0'
                      }}%
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </template>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>

const questionStore = useQuestionStore()
const segmentGroupStore = useSegmentGroupStore()

const props = defineProps({
  segmentGroup: {
    type: Object,
    required: true
  },
  selectedSegments: {
    type: Array,
    default: () => []
  },
  loading: {
    type: Boolean,
    default: false
  }
})

const emit = defineEmits(['update:selectedSegments'])

const { segmentGroup, loading } = toRefs(props)

// Initialize with all segments selected if none provided
const internalSelectedSegments = ref([])

watchEffect(() => {
  if (props.selectedSegments && props.selectedSegments.length > 0) {
    internalSelectedSegments.value = [...props.selectedSegments]
  } else if (segmentGroup.value?.segments) {
    // If no segments are selected, select all by default
    internalSelectedSegments.value = segmentGroup.value.segments.map(s => s.id)
  }
})

// Ensure we emit initial selected segments
onMounted(() => {
  if (internalSelectedSegments.value.length > 0) {
    emit('update:selectedSegments', internalSelectedSegments.value)
  }
})

const handleSegmentToggle = (newValue) => {
  internalSelectedSegments.value = newValue
  emit('update:selectedSegments', newValue)
}

const isSegmentSelected = (segmentId) => {
  return internalSelectedSegments.value.includes(segmentId)
}

const audienceBreakdown = computed(() => {
  return segmentGroupStore.getAudienceBreakdownForId(segmentGroup.value.id)
})

const sortedSegments = computed(() => {
  return audienceBreakdown.value.segments.sort((a, b) => b.count - a.count)
})

const question = computed(() => {
  return questionStore.getBySegmentGroupId(segmentGroup.value.id)
})

const questionText = computed(() => {
  if (!question.value) return ''
  const text = renderTextForQuestion(question.value)
  if (text === false) return ''
  return text
})

const needsSetup = computed(() => {
  return segmentGroup.value.segments.length === 0
})

const showSegmentName = ({ id: segmentId }) => {
  const segment = segmentGroup.value.segments.find((s) => s.id === segmentId)
  return segmentName(segment)
}
</script>